// src/screens/s3/AnnualReview.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 3 — Annual Review
// Part A: Retainer info (editable fees and dates, standing order card)
// Part B: Homework (two action items with checkboxes)
// Part C: Programme Complete card
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { ClientData } from '../../types/ClientData';
import { formatCurrency } from '../../utils/format';
import { downloadProgrammeSummary } from '../../utils/downloadPDF';
import CurrencyInput from '../../components/CurrencyInput';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function AnnualReview({ data, onUpdate }: Props) {
  const [hw1Complete, setHw1Complete] = useState(false);
  const [hw2Complete, setHw2Complete] = useState(false);

  const retainerFee = data.retainerFee || 0;
  const retainerStartDate = data.retainerStartDate || '';
  const annualReviewDate = data.annualReviewDate || '';

  const handleDownload = async () => {
    await downloadProgrammeSummary(data);
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-6">
      <h2 className="text-xl font-bold text-ma-navy mb-2">Annual Review</h2>
      <p className="text-gray-600 mb-8">
        Set up your annual review schedule and homework for the year ahead.
      </p>

      {/* Part A: Retainer Info */}
      <div className="mb-8">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
          Retainer Agreement
        </div>

        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <CurrencyInput
                label="Annual Retainer Fee"
                value={retainerFee}
                onChange={(v) => onUpdate({ retainerFee: v })}
              />
            </div>

            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-2">
                Review Date (MM/YYYY)
              </label>
              <input
                type="text"
                placeholder="01/2027"
                value={annualReviewDate}
                onChange={(e) => onUpdate({ annualReviewDate: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-ma-navy/20"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wide font-medium block mb-2">
              Retainer Start Date (MM/YYYY)
            </label>
            <input
              type="text"
              placeholder="04/2026"
              value={retainerStartDate}
              onChange={(e) => onUpdate({ retainerStartDate: e.target.value })}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-ma-navy/20"
            />
          </div>
        </div>

        {/* Standing Order Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide mb-2">Standing Order</p>
          <p className="text-sm text-blue-800">
            {retainerFee > 0 && retainerStartDate
              ? `£${retainerFee.toLocaleString()}/year from ${retainerStartDate}`
              : retainerFee > 0
                ? `£${retainerFee.toLocaleString()}/year — set a start date above`
                : 'Set the retainer amount to generate standing order details'}
          </p>
        </div>

        {/* What your retainer includes */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 mt-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
            What your retainer includes
          </p>
          <p className="text-gray-700 text-sm leading-relaxed">
            Every year we reconvene, update your numbers, and adjust the plan as your life
            and business evolve. In between sessions you have a direct line — for questions,
            decisions, and anything that changes.
          </p>
          {data.annualReviewDate && (
            <p className="text-sm font-semibold mt-3" style={{ color: '#001F8C' }}>
              Your annual review is scheduled for {data.annualReviewDate}.
            </p>
          )}
        </div>
      </div>

      {/* Part B: Homework */}
      <div className="mb-8">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 pb-2">
          Homework for the Year
        </div>

        <div className="space-y-3">
          {/* Action 1 */}
          <div className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition">
            <input
              type="checkbox"
              checked={hw1Complete}
              onChange={(e) => setHw1Complete(e.target.checked)}
              className="mt-1 w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
            />
            <div className="flex-1">
              <input
                type="text"
                value={data.s3HomeworkAction1 || ''}
                onChange={(e) => onUpdate({ s3HomeworkAction1: e.target.value })}
                placeholder="e.g. Review insurance policies"
                className={`text-sm font-medium bg-transparent border-none focus:outline-none w-full ${
                  hw1Complete ? 'line-through text-gray-400' : 'text-gray-800'
                }`}
              />
            </div>
          </div>

          {/* Action 2 */}
          <div className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition">
            <input
              type="checkbox"
              checked={hw2Complete}
              onChange={(e) => setHw2Complete(e.target.checked)}
              className="mt-1 w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
            />
            <div className="flex-1">
              <input
                type="text"
                value={data.s3HomeworkAction2 || ''}
                onChange={(e) => onUpdate({ s3HomeworkAction2: e.target.value })}
                placeholder="e.g. Update ISA contribution standing orders"
                className={`text-sm font-medium bg-transparent border-none focus:outline-none w-full ${
                  hw2Complete ? 'line-through text-gray-400' : 'text-gray-800'
                }`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Part C: Programme Complete */}
      <div className="mb-8">
        <div className="bg-ma-navy text-white rounded-2xl p-8 text-center">
          <p className="text-sm font-semibold uppercase tracking-wide mb-2">Programme Complete</p>
          <p className="text-lg leading-relaxed mb-6">
            Your three-session programme is now in motion. {data.clientName}, you have a clear path to freedom, and we're here to keep you on track every year.
          </p>

          {/* Download Button */}
          <button
            onClick={handleDownload}
            className="inline-block px-6 py-3 bg-white text-ma-navy rounded-lg text-sm font-semibold hover:bg-gray-100 transition"
          >
            ⬇ Download Annual Review Pack
          </button>
        </div>
      </div>
    </div>
  );
}
